import {
    IndexSymbol,
    OptionType,
    AnalysisMode,
    TimeGap,
    UserRole,
    UserStatus,
    SUPPORTED_SYMBOLS,
    SYMBOL_DISPLAY_NAMES,
    SYMBOL_METADATA,
    SYMBOL_VARIANTS,
    UPSTOX_API_CONFIG as SHARED_UPSTOX_API_CONFIG,
    UPSTOX_INSTRUMENT_KEYS,
    STRIKE_STEP,
    API_CONFIG as SHARED_API_CONFIG,
    HF_CONFIG as SHARED_HF_CONFIG,
    COLORS,
    MARKET_HOURS as SHARED_MARKET_HOURS,
    isMarketDay as sharedIsMarketDay,
    isMarketHours as sharedIsMarketHours,
    REFRESH_INTERVALS as SHARED_REFRESH_INTERVALS,
    STORAGE_KEYS as SHARED_STORAGE_KEYS,
    getCanonicalSymbol as sharedGetCanonicalSymbol,
    getStrikeStepForSymbol as sharedGetStrikeStepForSymbol,
    normalizeSymbolLookupKey as sharedNormalizeSymbolLookupKey,
    getSymbolLookupCandidates as sharedGetSymbolLookupCandidates,
    isSymbolLookupMatch as sharedIsSymbolLookupMatch,
} from '@options-oracle/types'

export {
    IndexSymbol,
    OptionType,
    AnalysisMode,
    TimeGap,
    UserRole,
    UserStatus,
    SUPPORTED_SYMBOLS,
    SYMBOL_DISPLAY_NAMES,
    SYMBOL_METADATA,
    SYMBOL_VARIANTS,
    UPSTOX_INSTRUMENT_KEYS,
    STRIKE_STEP,
    COLORS,
}

// ============================================================================
// 2. UPSTOX JSON API CONFIGURATION (Web Specific Proxy)
// ============================================================================
export const UPSTOX_API_CONFIG = {
    ...SHARED_UPSTOX_API_CONFIG,
    PROXY_URL: '/api/upstox',
    getApiUrl: (symbol: IndexSymbol, expiry: string): string => {
        const config = SHARED_UPSTOX_API_CONFIG.symbols[symbol]
        if (!config) throw new Error(`Unsupported symbol: ${symbol}`)

        const isServer = typeof window === 'undefined'
        const baseUrl = isServer ? SHARED_UPSTOX_API_CONFIG.REAL_URL : UPSTOX_API_CONFIG.PROXY_URL

        return `${baseUrl}?assetKey=${config.assetKeyEncoded}&strategyChainType=PC_CHAIN&expiry=${expiry}`
    },
}

// ============================================================================
// 2.2 UPSTOX v3 HISTORICAL CANDLE API CONFIGURATION
// ============================================================================
export const UPSTOX_CANDLE_API = {
    BASE_URL_V3: 'https://api.upstox.com/v3/historical-candle',
    BASE_URL_V2: 'https://api.upstox.com/v2/historical-candle',
    // v3 interval format (for NSE_INDEX keys)
    INTERVAL_MAP_V3: {
        '1minute': 'minutes/1',
        '3minute': 'minutes/3',
        '5minute': 'minutes/5',
        '15minute': 'minutes/15',
        '30minute': 'minutes/30',
        '60minute': 'hours/1',
        day: 'days/1',
        week: 'weeks/1',
        month: 'months/1',
    } as Record<string, string>,
    // v2 interval map kept as-is (v2 accepts 1minute, day etc. directly)
    INTERVAL_MAP: {
        '1minute': 'minutes/1',
        '3minute': 'minutes/3',
        '5minute': 'minutes/5',
        '15minute': 'minutes/15',
        '30minute': 'minutes/30',
        '60minute': 'hours/1',
        day: 'days/1',
        week: 'weeks/1',
        month: 'months/1',
    } as Record<string, string>,
    /**
     * NSE_FO|<number> (token IDs from PC_CHAIN analytics) only work with v2 API.
     * NSE_INDEX|... keys work with v3 API.
     */
    _isTokenKey: (instrumentKey: string): boolean => {
        return instrumentKey.startsWith('NSE_FO|') || instrumentKey.startsWith('BSE_FO|')
    },
    getHistoricalUrl: (instrumentKey: string, interval: string, toDate: string, fromDate: string): string => {
        const encoded = encodeURIComponent(instrumentKey)
        if (UPSTOX_CANDLE_API._isTokenKey(instrumentKey)) {
            // v2: /v2/historical-candle/{key}/{interval}/{to}/{from}
            return `${UPSTOX_CANDLE_API.BASE_URL_V2}/${encoded}/${interval}/${toDate}/${fromDate}`
        }
        const v3Interval = UPSTOX_CANDLE_API.INTERVAL_MAP_V3[interval] || 'minutes/1'
        return `${UPSTOX_CANDLE_API.BASE_URL_V3}/${encoded}/${v3Interval}/${toDate}/${fromDate}`
    },
    getIntradayUrl: (instrumentKey: string, interval: string = '1minute'): string => {
        const encoded = encodeURIComponent(instrumentKey)
        if (UPSTOX_CANDLE_API._isTokenKey(instrumentKey)) {
            // v2: /v2/historical-candle/intraday/{key}/{interval}
            return `${UPSTOX_CANDLE_API.BASE_URL_V2}/intraday/${encoded}/${interval}`
        }
        const v3Interval = UPSTOX_CANDLE_API.INTERVAL_MAP_V3[interval] || 'minutes/1'
        return `${UPSTOX_CANDLE_API.BASE_URL_V3}/intraday/${encoded}/${v3Interval}`
    },
}

export const REFRESH_INTERVALS = SHARED_REFRESH_INTERVALS
export const STORAGE_KEYS = SHARED_STORAGE_KEYS
export const MARKET_HOURS = SHARED_MARKET_HOURS
export const API_CONFIG = SHARED_API_CONFIG
export const HF_CONFIG = SHARED_HF_CONFIG

export function isMarketDay(date?: Date): boolean {
    return sharedIsMarketDay(date)
}

export function isMarketHours(): boolean {
    return sharedIsMarketHours()
}

export const getCanonicalSymbol = sharedGetCanonicalSymbol
export const getStrikeStepForSymbol = sharedGetStrikeStepForSymbol
export const normalizeSymbolLookupKey = sharedNormalizeSymbolLookupKey
export const getSymbolLookupCandidates = sharedGetSymbolLookupCandidates
export const isSymbolLookupMatch = sharedIsSymbolLookupMatch

// Legacy CONFIG bundle (Pruned)
export const CONFIG = {
    SYMBOLS: SUPPORTED_SYMBOLS,
    SYMBOL_NAMES: SYMBOL_DISPLAY_NAMES,
    SYMBOL_METADATA: SYMBOL_METADATA,
    SYMBOL_VARIANTS: SYMBOL_VARIANTS,
    UPSTOX_API: UPSTOX_API_CONFIG,
    STRIKE_STEP: STRIKE_STEP,
    API: API_CONFIG,
    HF: SHARED_HF_CONFIG,
    COLORS,
    MARKET: MARKET_HOURS,
    REFRESH: REFRESH_INTERVALS,
    UI: {
        ROWS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
        DEFAULT_ROWS_PER_PAGE: 10,
        TOAST_DURATION: 3000,
        STORAGE_KEYS: STORAGE_KEYS,
    },
}
